import React, { useEffect, useState } from 'react';
import { generateResults } from '../utils/openaiHelpers';
import { ScoreHeatmap } from './results/ScoreHeatmap';
import { ScoreCharts } from './results/ScoreCharts';
import { ResultSummary } from './results/ResultSummary';
import { RecommendationsList } from './results/RecommendationsList';
import { CompanySummary } from './results/CompanySummary';
import { Spinner } from './ui/Spinner';
interface ResultsProps {
  clientData: any;
  auditResponses: any;
  onRestart: () => void;
}
export const Results: React.FC<ResultsProps> = ({
  clientData,
  auditResponses,
  onRestart
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [results, setResults] = useState<any>(null);
  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        const resultsData = await generateResults(clientData, auditResponses);
        setResults(resultsData);
      } catch (err) {
        setError('Failed to generate results. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [clientData, auditResponses]);
  const handleDownloadPDF = () => {
    // This would be implemented with a PDF generation library
    alert('PDF download functionality would be implemented here');
  };
  const handleSendEmail = () => {
    // This would be implemented with an email service
    alert('Email functionality would be implemented here');
  };
  if (loading) {
    return <div className="flex flex-col items-center justify-center py-12" data-id="element-165">
        <Spinner size="lg" color="blue" data-id="element-166" />
        <p className="mt-4 text-gray-600" data-id="element-167">
          Analyzing your responses and generating insights...
        </p>
        <p className="text-sm text-gray-500 mt-2" data-id="element-168">
          This may take a moment as we prepare your personalized
          recommendations.
        </p>
      </div>;
  }
  if (error) {
    return <div className="bg-red-50 border-l-4 border-red-500 p-4" data-id="element-169">
        <p className="text-red-700" data-id="element-170">{error}</p>
        <button className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" onClick={() => window.location.reload()} data-id="element-171">
          Try Again
        </button>
      </div>;
  }
  return results ? <div data-id="element-172">
      <div className="flex justify-between items-center mb-8" data-id="element-173">
        <h2 className="text-2xl font-bold text-gray-800" data-id="element-174">
          Your Business Audit Results
        </h2>
        <div className="flex space-x-3" data-id="element-175">
          <button onClick={handleDownloadPDF} className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 text-sm hover:bg-gray-50 flex items-center" data-id="element-176">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-177">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" data-id="element-178" />
            </svg>
            Download PDF
          </button>
          <button onClick={handleSendEmail} className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 text-sm hover:bg-gray-50 flex items-center" data-id="element-179">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-180">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" data-id="element-181" />
            </svg>
            Email Report
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-182">
        <div className="col-span-2" data-id="element-183">
          <CompanySummary companyProfile={clientData.companyProfile} challenge={clientData.challenge} target={clientData.target} data-id="element-184" />
        </div>
        <div data-id="element-185">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-186">
            <h3 className="font-bold text-blue-800 mb-2" data-id="element-187">Overall Score</h3>
            <div className="text-4xl font-bold text-blue-700 mb-2" data-id="element-188">
              {results.overallScore}
              <span className="text-xl" data-id="element-189">/100</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-190">
              <div className={`h-2.5 rounded-full ${results.overallScore >= 80 ? 'bg-green-500' : results.overallScore >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
              width: `${results.overallScore}%`
            }} data-id="element-191"></div>
            </div>
            <div className="mt-4 text-sm text-blue-700" data-id="element-192">
              {results.overallScore >= 80 && 'Your business processes are strong. Focus on optimization.'}
              {results.overallScore >= 50 && results.overallScore < 80 && 'Your business has a foundation but needs improvement in key areas.'}
              {results.overallScore < 50 && 'Critical improvement needed across multiple business functions.'}
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-193">
        <ScoreHeatmap scores={results.functionalScores} data-id="element-194" />
        <ScoreCharts scores={results.functionalScores} data-id="element-195" />
      </div>
      <ResultSummary summary={results.summary} data-id="element-196" />
      <RecommendationsList recommendations={results.recommendations} data-id="element-197" />
      <div className="mt-12 flex justify-between items-center" data-id="element-198">
        <button onClick={onRestart} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-199">
          Start New Audit
        </button>
        <button onClick={handleSendEmail} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-200">
          Get Expert Help
        </button>
      </div>
    </div> : null;
};